// src/components/PlayerProfileModal.js - CANONICAL NESTED STRUCTURE ONLY
// Uses NESTED structure: player.ids.mlb, player.info.first_name, player.stats.season
// NO FALLBACKS - Backend always returns nested structure
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  X, DollarSign, User,
  TrendingUp, Shield,
  Maximize2, Minimize2
} from 'lucide-react';
import { dynastyTheme } from '../services/colorService';
import { formatBattingAvg, formatERA } from '../services/statFormatUtils';
import { formatContractDisplay } from '../services/contractUtils';
import { leaguesAPI } from '../services/apiService';
import apiService from '../services/apiService';
import { WatchListStar } from './WatchList';
import EligibilityBadges from './EligibilityBadges';
import MiniStatsBar from './player-profile/MiniStatsBar';
import StatsTab from './player-profile/StatsTab';
import InfoTab from './player-profile/InfoTab';
import FullTabContent from './player-profile/FullTabContent';
import AnalyticsGrid from './player-profile/AnalyticsGrid';
import { calculateAge } from '../services/playerUtils';
import logger from '../utils/logger';
import InjuryBadge from './InjuryBadge';
import { usePlayerNotes } from '../contexts/PlayerNotesContext';
import { useLeague } from '../contexts/LeagueContext';
import { isPitcherPosition } from '../utils/playerClassification';
import { getWaiverActionState } from '../utils/waiverStatus';


const PlayerProfileModal = ({
  playerId,
  leagueId,
  isOpen,
  onClose,
  userTeamId,
  onPlayerAction,
  isCommissionerMode = false,
  initialPlayer = null
}) => {
  const league = useLeague();
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(false);
  const [player, setPlayer] = useState(null);
  const [rosterStatus, setRosterStatus] = useState('checking');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [scoringCategories, setScoringCategories] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [mode, setMode] = useState('compact'); // 'compact' | 'full'
  const [fullTab, setFullTab] = useState('overview');
  const [contentVisible, setContentVisible] = useState(true);
  const advancedRankingsRef = useRef(null);
  const [advancedRankings, setAdvancedRankings] = useState(null);
  const [personalizedValue, setPersonalizedValue] = useState(null);
  const playerNotes = usePlayerNotes(player?.ids?.mlb);
  const prevModeRef = useRef('compact');

  // Reset mode when modal opens OR closes
  // Resetting on close prevents stale 'full' state from flashing on next open
  useEffect(() => {
    setMode('compact');
    setFullTab('overview');
    setContentVisible(true);
    prevModeRef.current = 'compact';
  }, [isOpen, playerId]);

  // Handle expand transition: crossfade content
  useEffect(() => {
    if (mode !== prevModeRef.current) {
      // Fade out content
      setContentVisible(false);
      // Fade in after 150ms delay
      const timer = setTimeout(() => {
        setContentVisible(true);
      }, 150);
      prevModeRef.current = mode;
      return () => clearTimeout(timer);
    }
  }, [mode]);

  // Esc key closes modal entirely
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // userTeamId is in the deps because loadPlayerData gates the
  // personalized-value fetch on it. If the prop arrives async from a parent,
  // the effect needs to re-run once it's known.
  useEffect(() => {
    if (isOpen && playerId) {
      setActionError(null);
      loadPlayerData();
      loadScoringCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, playerId, leagueId, userTeamId]);

  const loadScoringCategories = async () => {
    const defaultCats = {
      hitting: ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS'],
      pitching: ['W', 'SV', 'ERA', 'WHIP', 'K', 'QS']
    };
    setScoringCategories(defaultCats);

    if (leagueId) {
      if (league?.league_settings?.scoring_categories) {
        const cats = league.league_settings.scoring_categories;

        const normalizedCats = {
          hitting: cats.hitting || defaultCats.hitting,
          pitching: cats.pitching || defaultCats.pitching
        };

        setScoringCategories(normalizedCats);
        return;
      }

      try {
        const response = await leaguesAPI.getLeagueSettings(leagueId);

        if (response.success && response.settings?.scoring_categories) {
          const cats = response.settings.scoring_categories;

          const normalizedCats = {
            hitting: cats.hitting || cats.hitting || defaultCats.hitting,
            pitching: cats.pitching || cats.pitching || defaultCats.pitching
          };

          setScoringCategories(normalizedCats);
        }
      } catch (err) {
        logger.error('Error loading scoring categories:', err);
      }
    }
  };

  const loadPlayerData = async () => {
    setLoading(true);
    setPersonalizedValue(null);

    try {
      // Parallel-fetch: canonical player (for the modal), plus personalized
      // Fantasy Value (only when we have a viewing team + league). The PV call
      // runs alongside the modal load so it doesn't add latency.
      const modalP = leagueId
        ? apiService.get(`/api/players/leagues/${leagueId}/players/${playerId}/modal`)
        : apiService.get(`/api/players/${playerId}`);

      const pvP = (leagueId && userTeamId)
        ? leaguesAPI.getPersonalizedValue(leagueId, playerId, userTeamId).catch(() => null)
        : Promise.resolve(null);

      const [response, pvResult] = await Promise.all([modalP, pvP]);

      setPersonalizedValue(pvResult || null);

      const data = response.data || response;

      logger.debug('Raw API response:', data);

      if (data && data.player) {
        logger.debug('Loaded canonical player data:', data.player);

        // Use canonical NESTED structure directly
        setPlayer(data.player);

        // League context comes from backend automatically
        if (leagueId && data.player.league_context) {
          const ctx = data.player.league_context;

          // Determine roster status from league_context
          if (ctx.status === 'owned') {
            setRosterStatus(ctx.team?.is_user_team ? 'owned' : 'other_team');
          } else if (ctx.status === 'available') {
            setRosterStatus('available');
          } else {
            setRosterStatus('view_only');
          }
        } else if (!leagueId) {
          setRosterStatus('view_only');
        } else {
          setRosterStatus('view_only');
        }
      }
    } catch (err) {
      logger.error('Error fetching complete player data:', err);

      // Fallback to initial player if provided
      if (initialPlayer) {
        const ipIsOffseason = ['setup', 'draft', 'finalizing'].includes(initialPlayer.league_status);
        const ipContract = ipIsOffseason && initialPlayer.projected_contract
            ? initialPlayer.projected_contract
            : initialPlayer.league_context?.financial;
        const baselinePrice = ipContract?.baseline_price;

        setPlayer(initialPlayer);

        if (leagueId && (initialPlayer.league_context?.status === 'available' || initialPlayer.availability_status === 'available')) {
          setRosterStatus('available');
        } else if (leagueId) {
          setRosterStatus('other_team');
        } else {
          setRosterStatus('view_only');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isPitcher = () => {
    return player && player.info && isPitcherPosition(player.info.position);
  };
  const isPitcherBool = isPitcher();


  const getPerformanceStatus = () => {
    if (!player || !player.stats) return { status: 'neutral', text: 'No recent data' };

    const rolling = player.stats.rolling_14_day;
    if (!rolling) return { status: 'neutral', text: 'No recent data' };

    if (isPitcher()) {
      const era = rolling.pitching?.era || 0;
      if (era < 2.50) return { status: 'hot', text: 'Hot Streak' };
      if (era > 5.00) return { status: 'cold', text: 'Cold Streak' };
    } else {
      let opsValue = rolling.hitting?.ops;
      if (!opsValue && rolling.hitting?.batting_avg !== undefined) {
        const avg = rolling.hitting.batting_avg;
        const avgValue = avg < 1 ? avg : avg / 1000;
        if (avgValue > 0.300) return { status: 'hot', text: 'Hot Streak' };
        if (avgValue < 0.200) return { status: 'cold', text: 'Cold Streak' };
      } else if (opsValue) {
        if (opsValue > 0.850) return { status: 'hot', text: 'Hot Streak' };
        if (opsValue < 0.650) return { status: 'cold', text: 'Cold Streak' };
      }
    }

    return { status: 'neutral', text: 'Steady' };
  };

  const performance = getPerformanceStatus();

  const getActionButton = () => {
    if (loading || rosterStatus === 'checking' || !player) {
      return null;
    }

    if (rosterStatus === 'view_only') {
      return null;
    }

    // baseline_price is always on league_context.financial (the FA add price)
    const price = player.league_context?.financial?.baseline_price;

    let button = null;

    if (rosterStatus === 'available') {
      // Hide Add button during active minor league draft
      const mlDraftActive = league?.ml_draft_status === 'in_progress';
      // DD-WAIVER-0723-BUILD W2 (Bible § 4.5.19): on-waivers players get a
      // CLAIM affordance instead of ADD; the team that cut them sees a
      // disabled Cooling Off button until the hold expires. The 'add'
      // action itself is waiver-gated in PlayerModalContext.
      const waiverState = getWaiverActionState(player, userTeamId || null);
      const isActiveWaiver = waiverState.onWaivers && !waiverState.expired;
      if (mlDraftActive) {
        button = (
          <div className="text-center text-xs text-yellow-500 py-1">
            ML Draft in progress — use draft interface
          </div>
        );
      } else if (waiverState.coolingOff) {
        button = (
          <button
            disabled={true}
            title="You cut this player; wait for waivers to clear"
            className={`${dynastyTheme.components.button.primary} block mx-auto max-w-[240px] px-4 py-1 text-xs opacity-50 cursor-not-allowed`}
          >
            Cooling Off
          </button>
        );
      } else {
        button = (
          <button
            onClick={() => handlePlayerAction('add', player)}
            disabled={actionLoading}
            className={`${dynastyTheme.components.button.primary} block mx-auto max-w-[240px] px-4 py-1 text-xs`}
          >
            {isActiveWaiver ? `Claim Player - $${price}M` : `Add Player - $${price}M`}
          </button>
        );
      }
    } else if (rosterStatus === 'owned') {
      button = (
        <button
          onClick={() => handlePlayerAction('drop', player)}
          disabled={actionLoading}
          className={`${dynastyTheme.utils.getComponent('button', 'danger', 'xs')} w-full`}
        >
          Drop Player
        </button>
      );
    } else if (rosterStatus === 'other_team') {
      button = (
        <button
          onClick={() => handlePlayerAction('trade', player)}
          disabled={actionLoading}
          className={`${dynastyTheme.utils.getComponent('button', 'secondary', 'xs')} w-full`}
        >
          Propose Trade
        </button>
      );
    }

    if (!button) return null;

    return (
      <div>
        {button}
        {actionError && (
          <div className="mt-2 px-3 py-2 rounded bg-red-500/15 border border-red-500/30 text-red-400 text-xs">
            {actionError}
          </div>
        )}
      </div>
    );
  };

  const handlePlayerAction = async (action, playerData) => {
    setActionLoading(true);
    setActionError(null);
    try {
      if (onPlayerAction) {
        const result = await onPlayerAction(action, playerData);
        if (result?.success) {
          window.dispatchEvent(new Event('roster-updated'));
          onClose();
        } else if (!result?.cancelled) {
          setActionError(result?.message || `Failed to ${action} player`);
        }
        // If cancelled, do nothing — modal stays open
      }
    } catch (err) {
      logger.error(`Error ${action} player:`, err);
      setActionError(err.message || `Failed to ${action} player`);
    } finally {
      setActionLoading(false);
    }
  };

  const renderStatsRow = (label, statsData) => {
    if (!statsData) return null;

    const isAccrued = label === 'Accrued';
    const isSeason = label === 'Season';
    const pitcher = isPitcher();

    const getStatData = () => {
      return pitcher ? statsData.pitching || {} : statsData.hitting || {};
    };

    const statSource = getStatData();

    const getColumns = () => {
      if (pitcher) {
        const baseCols = ['G', 'GS', 'IP'];
        const leagueCats = scoringCategories?.pitching || ['W', 'SV', 'ERA', 'WHIP', 'K', 'QS'];
        return [...baseCols, ...leagueCats];
      } else {
        const baseCols = ['AB', 'H', 'BB', 'SO'];
        const leagueCats = scoringCategories?.hitting || ['R', 'HR', 'RBI', 'SB', 'AVG', 'OPS'];
        return [...baseCols, ...leagueCats];
      }
    };

    const getValue = (stat) => {
      if (!statSource) return 0;

      const statUpper = stat.toUpperCase();

      if (pitcher) {
        const fieldMap = {
          'G': 'games_played',
          'GS': 'games_started',
          'IP': 'innings_pitched',
          'W': 'wins',
          'L': 'losses',
          'SV': 'saves',
          'S': 'saves',
          'ERA': 'era',
          'WHIP': 'whip',
          'K': 'strikeouts_pitched',
          'SO': 'strikeouts_pitched',
          'QS': 'quality_starts'
        };

        const field = fieldMap[statUpper];
        const value = statSource[field] || 0;

        if (statUpper === 'ERA') return formatERA(value);
        if (statUpper === 'WHIP') return formatBattingAvg(value);
        if (statUpper === 'IP') return value.toFixed(1);
        return value;
      } else {
        const fieldMap = {
          'AB': 'at_bats',
          'H': 'hits',
          'BB': 'walks',
          'SO': 'strikeouts',
          'R': 'runs',
          'HR': 'home_runs',
          'RBI': 'rbi',
          'SB': 'stolen_bases',
          'AVG': 'batting_avg',
          'OPS': 'ops'
        };

        const field = fieldMap[statUpper];
        const value = statSource[field] || 0;

        if (statUpper === 'AVG') return formatBattingAvg(value);
        if (statUpper === 'OPS') return formatBattingAvg(value);
        return value;
      }
    };

    const columns = getColumns();

    return (
      <tr className={label === 'Accrued' ? 'bg-yellow-400/5' : ''}>
        <td className={`py-2 px-3 text-sm font-medium ${dynastyTheme.classes.text.neutralLight} text-left`}>
          {label}
        </td>
        {columns.map((col) => (
          <td key={col} className={`py-2 px-3 text-sm text-center ${
            label === 'Accrued' ? 'text-yellow-400 font-semibold' : dynastyTheme.classes.text.white
          }`}>
            {getValue(col)}
          </td>
        ))}
      </tr>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // COMPACT MODE — Existing small modal content (unchanged)
  // ═══════════════════════════════════════════════════════════


  // ═══════════════════════════════════════════════════════════
  // FULL MODE — Large modal content
  // ═══════════════════════════════════════════════════════════

  const fullTabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'gameLogs', label: 'Game Logs' },
    { key: 'career', label: 'Career & History' },
    { key: 'analytics', label: 'Analytics' },
    ...(leagueId ? [{ key: 'eligibility', label: 'Eligibility' }] : []),
    { key: 'notes', label: 'Notes' },
    ...(leagueId ? [{ key: 'contract', label: 'Contract' }] : [])
  ];


  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  const isCompact = mode === 'compact';

  const modalContent = (
    <div className="fixed inset-0" style={{ zIndex: 999999 }}>
      {/* Backdrop — shared between modes */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative flex items-center justify-center min-h-screen p-4">
        {/* Modal container — transitions between compact and full sizes */}
        <div
          className={`
            relative ${dynastyTheme.components.card.base}
            shadow-2xl border border-neutral-600 rounded-xl
            ${isCompact ? 'overflow-y-auto' : 'overflow-hidden'}
          `}
          style={{
            width: isCompact ? '100%' : 'min(1280px, 90vw)',
            maxWidth: isCompact ? '672px' : 'min(1280px, 90vw)',
            height: isCompact ? 'auto' : 'min(880px, 88vh)',
            maxHeight: isCompact ? '90vh' : 'min(880px, 88vh)',
            transition: 'width 300ms ease-out, max-width 300ms ease-out, height 300ms ease-out, max-height 300ms ease-out',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Content crossfade wrapper */}
          <div
            style={{
              opacity: contentVisible ? 1 : 0,
              transition: 'opacity 150ms ease-in-out',
            }}
            className={isCompact ? '' : 'h-full flex flex-col'}
          >
            {/* ══════ COMPACT MODE ══════ */}
            {isCompact && (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-neutral-700">
                  <div className="flex items-center gap-3">
                    <User className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
                    <h2 className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
                      Player Profile
                    </h2>
                  </div>
                  <button
                    onClick={onClose}
                    className={`p-2 rounded-lg ${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.bg.neutral} transition-colors`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {loading && (
                  <div className="p-8 text-center">
                    <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${dynastyTheme.classes.border.primary} mx-auto mb-4`}></div>
                    <p className={dynastyTheme.classes.text.neutralLight}>Loading complete player data...</p>
                  </div>
                )}

                {!loading && player && (
                  <>
                    {/* Player Header */}
                    <div className="p-4 text-center border-b border-neutral-700">
                      <div className="mb-3 flex items-start justify-center gap-6">
                        <img
                          src={`https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_96,h_96,c_fill,g_face,q_auto:best/v1/people/${player.ids?.mlb}/headshot/67/current`}
                          alt={`${player.info?.first_name} ${player.info?.last_name}`}
                          className="w-24 h-24 rounded-full border-2 border-yellow-400 bg-neutral-800 object-cover"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(player.info?.first_name || '')}+${encodeURIComponent(player.info?.last_name || '')}&background=facc15&color=000&size=96`;
                          }}
                        />
                        {/* Financial Info - Only for owned players */}
                        {rosterStatus === 'owned' && player.league_context && (() => {
                          const hdrIsOffseason = ['setup', 'draft', 'finalizing'].includes(player.league_status);
                          const hdrContract = hdrIsOffseason && player.projected_contract
                              ? player.projected_contract
                              : player.league_context?.financial;
                          // projected_contract uses 'salary', league_context.financial uses 'contract_salary'
                          const hdrSalary = hdrContract?.contract_salary ?? hdrContract?.salary;
                          // Market value is only on league_context.financial, not projected_contract
                          const hdrMarket = player.league_context?.financial?.current_market_value;
                          return (
                          <div className="text-left space-y-2">
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-green-400" />
                              <span className={dynastyTheme.classes.text.neutralLight}>Salary:</span>
                              <span className="text-green-400 font-semibold">{hdrSalary != null ? `$${hdrSalary}` : '--'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Shield className="w-4 h-4 text-blue-400" />
                              <span className={dynastyTheme.classes.text.neutralLight}>Contract:</span>
                              <span className="text-blue-400 font-semibold">{formatContractDisplay(hdrContract?.contract_type, hdrContract?.contract_years)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-yellow-400" />
                              <span className={dynastyTheme.classes.text.neutralLight}>Market:</span>
                              <span className="text-yellow-400 font-semibold">{hdrMarket > 0 ? `$${hdrMarket}` : '--'}</span>
                            </div>
                          </div>
                          );
                        })()}
                      </div>

                      <h3 className={`text-2xl font-bold ${dynastyTheme.classes.text.white} mb-1 flex items-center justify-center gap-2`}>
                        {player.info?.first_name} {player.info?.last_name}
                        <InjuryBadge mlbId={player.ids?.mlb} />
                        <WatchListStar
                          playerId={player.ids?.mlb}
                          size={20}
                        />
                      </h3>

                      <div className="flex items-center justify-center gap-4 text-sm">
                        <span className={`${dynastyTheme.classes.text.primary} font-medium`}>
                          {player.info?.position}
                        </span>
                        <span className={dynastyTheme.classes.text.neutralLight}>
                          {player.info?.mlb_team || 'Free Agent'}
                        </span>
                        <span className={dynastyTheme.classes.text.neutralLight}>
                          Age {calculateAge(player.info?.birthdate)}
                        </span>
                      </div>
                      <div className="mt-1.5">
                        <EligibilityBadges player={player} />
                      </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b border-neutral-700">
                      <button
                        onClick={() => setActiveTab('info')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                          activeTab === 'info'
                            ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primary}`
                            : `${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white}`
                        }`}
                      >
                        Info & Analytics
                      </button>
                      <button
                        onClick={() => setActiveTab('stats')}
                        className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                          activeTab === 'stats'
                            ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primary}`
                            : `${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white}`
                        }`}
                      >
                        Stats
                      </button>
                    </div>

                    {/* Tab Content */}
                    <div className="p-4">
                      {activeTab === 'info' ? (
                        <InfoTab
                          player={player}
                          performance={performance}
                          isPitcher={isPitcher}
                        />
                      ) : (
                        <StatsTab
                          player={player}
                          isPitcher={isPitcher}
                          scoringCategories={scoringCategories}
                          rosterStatus={rosterStatus}
                          renderStatsRow={renderStatsRow}
                        />
                      )}
                    </div>

                    {/* Action Button and Expand Profile Link */}
                    <div className="p-4 border-t border-neutral-700">
                      {leagueId && getActionButton()}

                      <div className="text-center pt-3">
                        <button
                          onClick={() => setMode('full')}
                          className={`${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.primary} text-sm ${dynastyTheme.classes.transitionFast} flex items-center justify-center gap-1.5 mx-auto`}
                        >
                          <Maximize2 className="w-3.5 h-3.5" />
                          View Full Profile
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {!loading && !player && (
                  <div className="p-8 text-center">
                    <p className={dynastyTheme.classes.text.error}>Player not found</p>
                    <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-2`}>
                      Unable to load player data. Please try again.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* ══════ FULL MODE ══════ */}
            {!isCompact && (
              <>
                {loading && (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${dynastyTheme.classes.border.primary} mx-auto mb-4`}></div>
                      <p className={dynastyTheme.classes.text.neutralLight}>Loading player data...</p>
                    </div>
                  </div>
                )}

                {!loading && player && (
                  <>
                    {/* ─── Sticky Header ─── */}
                    <div className="flex-shrink-0 sticky top-0 z-10" style={{ backgroundColor: 'inherit' }}>
                      {/* Player Identity Row (~64px) */}
                      <div className="flex items-center gap-4 px-5 py-3 border-b border-neutral-700/50">
                        <img
                          src={`https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_64,h_64,c_fill,g_face,q_auto:best/v1/people/${player.ids?.mlb}/headshot/67/current`}
                          alt={`${player.info?.first_name} ${player.info?.last_name}`}
                          className="w-14 h-14 rounded-full border-2 border-yellow-400 bg-neutral-800 object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(player.info?.first_name || '')}+${encodeURIComponent(player.info?.last_name || '')}&background=facc15&color=000&size=64`;
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h2 className={`text-xl font-bold ${dynastyTheme.classes.text.white} truncate`}>
                              {player.info?.first_name} {player.info?.last_name}
                            </h2>
                            <InjuryBadge mlbId={player.ids?.mlb} />
                            {/* Hot/Cold badge */}
                            {performance.status !== 'neutral' && (
                              <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${
                                performance.status === 'hot'
                                  ? 'bg-orange-500/20 text-orange-400'
                                  : 'bg-blue-500/20 text-blue-400'
                              }`}>
                                {performance.status === 'hot' ? 'HOT' : 'COLD'}
                              </span>
                            )}
                            <WatchListStar playerId={player.ids?.mlb} size={18} />
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className={`${dynastyTheme.classes.text.primary} font-medium`}>
                              {player.info?.position}
                            </span>
                            <span className={dynastyTheme.classes.text.neutralLight}>
                              {player.info?.mlb_team || 'Free Agent'}
                            </span>
                            <span className={dynastyTheme.classes.text.neutralLight}>
                              Age {calculateAge(player.info?.birthdate)}
                            </span>
                            {(() => {
                              if (rosterStatus !== 'owned') return null;
                              const compactIsOffseason = ['setup', 'draft', 'finalizing'].includes(player.league_status);
                              const compactContract = compactIsOffseason && player.projected_contract
                                  ? player.projected_contract
                                  : player.league_context?.financial;
                              if (!compactContract) return null;
                              const displaySalary = compactContract.contract_salary ?? compactContract.salary;
                              // Market value lives on league_context.financial, not projected_contract
                              const displayMarket = player.league_context?.financial?.current_market_value
                                || player.league_context?.financial?.baseline_price;
                              return (
                              <>
                                <span className="text-neutral-600">|</span>
                                <span className="text-green-400 font-medium text-xs">
                                  {displaySalary != null ? `$${displaySalary} salary` : ''}
                                </span>
                                <span className="text-neutral-600">/</span>
                                <span className="text-yellow-400 font-medium text-xs">
                                  {displayMarket > 0 ? `$${displayMarket} market` : '--'}
                                </span>
                                <span className={`${dynastyTheme.classes.text.neutralLight} text-xs`}>
                                  {formatContractDisplay(compactContract.contract_type, compactContract.contract_years)}
                                </span>
                              </>
                              );
                            })()}
                          </div>
                        </div>
                        {/* Minimize + Close buttons */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => setMode('compact')}
                            title="Minimize"
                            className={`p-2 rounded-lg ${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white} hover:bg-neutral-700 transition-colors`}
                          >
                            <Minimize2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={onClose}
                            title="Close"
                            className={`p-2 rounded-lg ${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white} hover:bg-neutral-700 transition-colors`}
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      {/* Mini Stats Bar (~28px) */}
                      <MiniStatsBar player={player} isPitcher={isPitcher} />

                      {/* Analytics Grid (80-120px) */}
                      <AnalyticsGrid
                        player={player}
                        isPitcher={isPitcherBool}
                        leagueId={leagueId}
                        personalizedValue={personalizedValue}
                        statsSeasonYear={
                          league?.current_season
                            ? ['setup', 'draft', 'finalizing'].includes(league?.league_status)
                              ? league.current_season - 1
                              : league.current_season
                            : undefined
                        }
                      />

                      {/* Tab Bar (~28px) */}
                      <div className="flex border-b border-neutral-700 px-4">
                        {fullTabs.map((tab) => (
                          <button
                            key={tab.key}
                            onClick={() => setFullTab(tab.key)}
                            className={`px-4 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                              fullTab === tab.key
                                ? `${dynastyTheme.classes.text.primary} border-b-2 ${dynastyTheme.classes.border.primary}`
                                : `${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white}`
                            }`}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* ─── Content Area ─── */}
                    {/* Game Logs: flex fill + hidden overflow (tab handles its own scroll) */}
                    {/* Other tabs: overflow-y-auto for normal scrolling */}
                    <div className={`flex-1 min-h-0 ${fullTab === 'gameLogs' ? 'overflow-hidden flex flex-col' : 'overflow-y-auto'}`}>
                      <FullTabContent
                        fullTab={fullTab}
                        player={player}
                        playerId={playerId}
                        leagueId={leagueId}
                        isPitcher={isPitcherBool}
                        userTeamId={userTeamId}
                        playerNotes={playerNotes}
                        advancedRankingsRef={advancedRankingsRef}
                        setAdvancedRankings={setAdvancedRankings}
                        statsSeasonYear={
                          league?.current_season
                            ? ['setup', 'draft', 'finalizing'].includes(league?.league_status)
                              ? league.current_season - 1
                              : league.current_season
                            : undefined
                        }
                      />
                    </div>

                    {/* ─── Action Bar (sticky bottom, league only) ─── */}
                    {leagueId && rosterStatus !== 'view_only' && (
                      <div className="flex-shrink-0 border-t border-neutral-700 px-5 py-1">
                        {getActionButton()}
                      </div>
                    )}
                  </>
                )}

                {!loading && !player && (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                      <p className={dynastyTheme.classes.text.error}>Player not found</p>
                      <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-2`}>
                        Unable to load player data. Please try again.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default PlayerProfileModal;
